import React, { useMemo, useRef, useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "highcharts/highcharts-3d";
import { RiBarChart2Line } from "react-icons/ri";
import { currencyDisplay } from "../helpers/commonHelper";

const PaymentPieChart = ({ paymentData, currency = "AED" }) => {
  const chartComponentRef = useRef(null);
  const [sliceColors, setSliceColors] = useState([]);

  // ✅ Convert data to Highcharts format
  const formattedData = useMemo(() => {
    return paymentData.map((item) => ({
      name: item.paymentDesc,
      y: Number(item.amount),
    }));
  }, [paymentData]);

  // ✅ Extract slice colors (same as your first chart)
  useEffect(() => {
    const chart = chartComponentRef.current?.chart;
    if (chart) {
      const colors = chart.series[0].points.map((p) => p.color);
      setSliceColors(colors);
    }
  }, [formattedData]);

  const options = {
    credits: { enabled: false },

    chart: {
      type: "pie",
      backgroundColor: "transparent",
      options3d: {
        enabled: true,
        alpha: 45,
        beta: 0,
      },
    },

    title: { text: "" },

    plotOptions: {
      pie: {
        depth: 75,
        innerSize: "55%",
        size: "80%",
        center: ["50%", "45%"],
        dataLabels: {
          enabled: true,
          format: "{point.name}",
          distance: 5,
          style: {
            fontFamily: "QSemiBold, sans-serif",
            fontSize: "12px",
            color: "#374151",
            textOutline: "none",
          },
          filter: {
            property: "percentage",
            operator: ">",
            value: 5,
          },
        },
      },
    },

    legend: { enabled: false },

    tooltip: {
      pointFormatter: function () {
        return `<b>${this.name}</b><br/>${currency} ${currencyDisplay(
          this.y
        )}`;
      },
    },

    series: [
      {
        data: formattedData,
      },
    ],
  };

  return (
    <div className="bg-white rounded-xl my-8 shadow-sm border border-gray-200 p-5">
      {/* Header */}
      <div className="pb-3 border-b border-gray-200 flex items-center">
        <RiBarChart2Line className="text-indigo-500 text-xl mr-2" />
        <h3 className="font-QBold text-gray-800">
          Daily Payment Summary
        </h3>
      </div>

      {/* Chart */}
      <div className="h-[320px] w-full mt-4">
        <HighchartsReact
          highcharts={Highcharts}
          options={options}
          ref={chartComponentRef}
        />
      </div>

      {/* ✅ Detail Cards (matching colors) */}
      <div className="grid grid-cols-2 gap-4 mt-6">
        {formattedData.map((item, index) => (
          <div
            key={item.name}
            className="flex border bg-gray-50 p-3 rounded-xl items-center gap-3"
          >
            {/* Color dot */}
            <div
              className="w-4 h-4 rounded-full"
              style={{ background: sliceColors[index] || "#ccc" }}
            />

            {/* Text */}
            <div>
              <p className="text-xs font-QSemiBold text-gray-700">
                {item.name} ({currency})
              </p>
              <p className="text-sm font-QBold text-gray-900">
                {currencyDisplay(item.y)}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PaymentPieChart;